import Layout from "@/components/Layout";
import PageHead from "@/components/PageHead";
import AnimatedSection from "@/components/AnimatedSection";
import { Shield, Scale, Building2, FileText, AlertTriangle } from "lucide-react";

const areas = [
  {
    icon: Building2,
    title: "Merger & Transaction Antitrust",
    items: [
      "Pre-transaction antitrust risk assessment",
      "Deal strategy and regulatory risk analysis",
      "Hart-Scott-Rodino review",
      "Merger investigations and second requests",
      "Strategic advice during merger challenges",
    ],
  },
  {
    icon: Shield,
    title: "Government Investigations",
    desc: "Representation in investigations involving the U.S. Department of Justice Antitrust Division, the Federal Trade Commission, and State Attorneys General.",
    items: [
      "Civil investigative demands",
      "Subpoenas and document requests",
      "Negotiations with enforcement agencies",
      "State Attorneys General investigations",
      "Strategy for responding to government investigations",
    ],
  },
  {
    icon: Scale,
    title: "Antitrust Litigation",
    items: [
      "Federal antitrust litigation",
      "Private antitrust litigation",
      "Monopolization claims",
      "Competitor disputes",
      "Class actions",
      "Distribution and vertical restraint litigation",
    ],
  },
  {
    icon: FileText,
    title: "Antitrust Counseling",
    items: [
      "Pricing and discount programs",
      "Distribution systems and dealer relationships",
      "Exclusive supply or licensing agreements",
      "Joint ventures and competitor collaborations",
      "Compliance programs and internal risk reviews",
    ],
  },
  {
    icon: AlertTriangle,
    title: "FTC Consumer Protection Claims",
    items: [
      "FTC investigations involving unfair competition and deceptive practices",
      "Engagement with Bureau of Consumer Protection staff and consent order negotiations",
      "Litigation in federal court and before Administrative Law Judges",
      "Compliance counseling on advertising, dark patterns, health claims, and endorsements",
      "Proactive risk assessment and compliance program design",
    ],
  },
];

const PracticeAreas = () => (
  <Layout>
    <PageHead
      title="Antitrust Practice Areas"
      description="Alicia J. Batts and Batts Legal LLP offer focused antitrust practice areas: merger review, DOJ & FTC investigations, antitrust litigation, competition counseling, and consumer protection."
      path="/practice-areas"
      keywords="Alicia Batts antitrust practice, antitrust merger review, FTC investigation lawyer, DOJ antitrust attorney, competition law practice areas, antitrust counseling, Batts Legal"
      jsonLd={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Antitrust Practice Areas — Alicia Batts | Batts Legal LLP",
        description: "Focused antitrust expertise across mergers, government investigations, litigation, counseling, and consumer protection led by Alicia J. Batts.",
        url: "https://www.battslegal.com/practice-areas",
      }}
    />
    <section className="bg-hero text-primary-foreground py-20">
      <div className="editorial-container">
        <AnimatedSection animation="fade" delay={0}>
          <div className="gold-accent-line mb-6" />
        </AnimatedSection>
        <AnimatedSection animation="fade-up" delay={100}>
          <h1 className="text-4xl md:text-5xl font-serif font-semibold mb-4">Practice Areas</h1>
        </AnimatedSection>
        <AnimatedSection animation="fade-up" delay={200}>
          <p className="text-lg opacity-80 max-w-2xl">Focused antitrust expertise across the full spectrum of competition law challenges facing corporations and investors.</p>
        </AnimatedSection>
      </div>
    </section>
    <section className="section-padding">
      <div className="editorial-container space-y-16">
        {areas.map((area, i) => (
          <AnimatedSection key={area.title} animation="fade-up" delay={i * 80}>
            <div className="grid md:grid-cols-3 gap-8 items-start">
              <div>
                <area.icon className="w-8 h-8 text-gold mb-4" />
                <h2 className="font-serif text-2xl font-semibold text-foreground">{area.title}</h2>
              </div>
              <div className="md:col-span-2">
                {area.desc && <p className="text-muted-foreground leading-relaxed mb-4">{area.desc}</p>}
                {area.items.length > 0 && (
                  <ul className="space-y-2">
                    {area.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-foreground">
                        <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </AnimatedSection>
        ))}
      </div>
    </section>
  </Layout>
);

export default PracticeAreas;
